class AddOnBlock extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleAddonQtyInput = this.handleAddonQtyInput.bind(this);
    this.handleAddonQtyChange = this.handleAddonQtyChange.bind(this);
    this.handleFormQuantityAdjust = this.handleFormQuantityAdjust.bind(this);
    this.handleMainQtyButtonClick = this.handleMainQtyButtonClick.bind(this);

    this._productCache = null;
    this._variantPriceMap = null;
    this._isAdding = false;
  }

  connectedCallback() {
    this.form = this.closest('#AddToCartForm');
    this.addEventListener('change', this.handleChange);
    this.addEventListener('input', this.handleAddonQtyInput);
    this.addEventListener('change', this.handleAddonQtyChange);
    this.addEventListener('click', this.handleClick);

    if (this.form && !this._submitListenerAdded) {
      this.form.addEventListener('submit', this.handleFormSubmit, true);
    }

    if (this.form) {
      this.form.addEventListener('input', this.handleFormQuantityAdjust);
      this.form.addEventListener('change', this.handleFormQuantityAdjust);
      this.form.addEventListener('click', this.handleMainQtyButtonClick);
    }

    this._cacheProductData();

    if (this.form && !this.form.querySelector('[name="id"]')) {
      let initialId = this.resolveVariantFromOptions();
      if (!initialId) initialId = this.resolveDefaultVariantId();

      if (initialId) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'id';
        hidden.value = initialId;
        this.form.appendChild(hidden);
      }
    }

    const moreInfoLinks = this.querySelectorAll('.add-on-block__more-info');

    if (moreInfoLinks && moreInfoLinks.length) {
      moreInfoLinks.forEach(link => {
        link.addEventListener('click', this.handleMoreInfoClick);
      });
    }

    this.syncSelectedState();
    this.updateMainVariantDiffs();
    this.updateProductPriceDisplay();
  }

  disconnectedCallback() {
    if (this.form && this._submitListenerAdded) {
      this.form.removeEventListener('submit', this.handleFormSubmit, true);
    }
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('input', this.handleAddonQtyInput);
    this.removeEventListener('change', this.handleAddonQtyChange);
    this.removeEventListener('click', this.handleClick);

    if (this.form) {
      this.form.removeEventListener('input', this.handleFormQuantityAdjust);
      this.form.removeEventListener('change', this.handleFormQuantityAdjust);
      this.form.removeEventListener('click', this.handleMainQtyButtonClick);
    }
  }

  handleMainQtyButtonClick(event) {
    if (!event.target.closest?.('.js-qty__adjust')) return;
    requestAnimationFrame(() => {
      this.updateProductPriceDisplay();
    });
  }

  handleFormQuantityAdjust(event) {
    const target = event.target;
    if (
      !target ||
      (target.name !== 'quantity' &&
        target.name !== 'quantity-js' &&
        !target.classList?.contains('js-qty__num'))
    ) {
      return;
    }
    this.updateProductPriceDisplay();
  }

  handleFormSubmit(event) {
    const form = event.currentTarget;
    if (!form || form.tagName !== 'FORM') return;

    const action = (form.getAttribute('action') || '').toLowerCase();
    if (action.includes('/cart') && !action.includes('/cart/add')) return;

    const addonSelections = this.getSelectedAddonSelections(form);
    if (!addonSelections.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.addToCartWithAddons(form, addonSelections);
  }

  _cacheProductData() {
    const script = document.querySelector(
      'script.product-json, script[id^="ProductJson-"]'
    );

    if (!script?.textContent) return;

    try {
      this._productCache = JSON.parse(script.textContent);

      if (this._productCache?.variants) {
        this._variantPriceMap = new Map(
          this._productCache.variants.map((variant) => [
            String(variant.id),
            variant.price
          ])
        );
      }
    } catch {
      this._productCache = null;
      this._variantPriceMap = null;
    }
  }

  async addToCartWithAddons(form, addonSelections) {
    if (this._isAdding) return;
    this._isAdding = true;

    const mainIdInput =
      form.querySelector('[name="id"]:checked') ||
      form.querySelector('[name="id"]');

    const mainVariantId = mainIdInput?.value;
    const quantity = this.getMainProductQuantity();

    if (!mainVariantId) {
      this._isAdding = false;
      return;
    }

    const parentId = parseInt(mainVariantId, 10);
    const bundleId = this.createBundleId();

    const items = [
      {
        id: parentId,
        quantity,
        properties: {
          _bundle_id: bundleId
        }
      },
      ...addonSelections.map((selection) => {
        const unitQty = Math.max(1, parseInt(selection.unitQty, 10) || 1);
        return {
          id: parseInt(selection.variantId, 10),
          quantity: unitQty * quantity,
          parent_id: parentId,
          properties: {
            _parent_id: String(parentId),
            _bundle_id: bundleId,
            _addon_unit_qty: String(unitQty)
          }
        };
      })
    ];

    const submitBtn = form.querySelector('#AddToCart');
    const cartAction =
      document.getElementById('PageContainer')?.dataset?.cartAction || 'drawer';

    const cartUrl = submitBtn?.dataset?.cartUrl || '/cart';

    const errorEl =
      form.querySelector('.qty-error') ||
      form.querySelector('.errors');

    const setLoading = (loading) => {
      if (!submitBtn) return;
      submitBtn.disabled = loading;
      submitBtn.classList.toggle('disabled', loading);
    };

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.style.display = '';
      errorEl.textContent = message;
    };

    const clearError = () => {
      if (!errorEl) return;
      errorEl.style.display = 'none';
      errorEl.textContent = '';
    };

    clearError();
    setLoading(true);

    const root = window.Shopify?.routes?.root ?? '/';
    const addUrl = `${root.replace(/\/$/, '')}/cart/add.js`;

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({ items })
      });

      const result = await response.json();

      if (!response.ok) {
        showError(
          result.description ||
          result.message ||
          'Unable to add to cart'
        );
        setLoading(false);
        this._isAdding = false;
        return;
      }

      if (cartAction === 'page') {
        window.location.href = cartUrl;
        return;
      }

      const cartResponse = await fetch(
        `${root.replace(/\/$/, '')}/cart.js`,
        { credentials: 'same-origin' }
      );

      const cart = await cartResponse.json();

      if (typeof window.wetheme !== 'undefined') {
        window.wetheme.updateCartDrawer(cart);

        if (cartAction === 'drawer') {
          window.wetheme.toggleRightDrawer('cart', true, { cart });
        }
      }

      setLoading(false);
      this._isAdding = false;

    } catch (err) {
      showError(err.message || 'Unable to add to cart');
      setLoading(false);
      this._isAdding = false;
    }
  }

  createBundleId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch {}
    return `bundle_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  resolveVariantFromOptions() {
    const variants = this._productCache?.variants;
    if (!variants?.length) return null;
  
    const selectedInputs = Array.from(
      this.querySelectorAll(
        'input.add-on-block__option-input[name^="option-"]:checked'
      )
    );
  
    if (!selectedInputs.length) return null;
  
    const matchedVariant = variants.find((variant) =>
      selectedInputs.every((selectedOptionInput) => {
        const optionIndex = selectedOptionInput.name.split('-')[1];
        return String(variant[`option${optionIndex}`]) === String(selectedOptionInput.value);
      })
    );
    return matchedVariant ? String(matchedVariant.id) : null;
  }

  resolveDefaultVariantId() {
    const product = this._productCache;
    if (!product) return null;

    const selectedId =
      product?.selected_or_first_available_variant?.id ??
      product?.selected_variant?.id;
    if (selectedId != null && selectedId !== '') return String(selectedId);

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;

    const firstAvailable =
      variants.find(v => v && v.available) ?? variants[0];
    return firstAvailable?.id != null ? String(firstAvailable.id) : null;
  }

  getSelectedAddonSelections(form) {
    const targetForm = form || this.form;
    if (!targetForm) return [];

    const checked = this.querySelectorAll(
      'input.add-on-block__option-input[value="with"]:checked[data-variant-id]'
    );

    return Array.from(checked)
      .map((withOptionInput) => {
        const variantId = withOptionInput.dataset.variantId;
        if (!variantId) return null;

        const card = withOptionInput.closest('.add-on-block__option')?.querySelector('.add-on-block__option-card');
        const qtyInput = card?.querySelector('input[data-addon-qty-input]');
        const maxAttr = qtyInput?.getAttribute('max');
        const max = Math.max(1, parseInt(maxAttr, 10) || 999);

        let unitQty = 1;
        if (qtyInput) {
          unitQty = parseInt(qtyInput.value, 10) || 1;
          unitQty = Math.max(1, Math.min(max, unitQty));
          qtyInput.value = String(unitQty);
        }

        return { variantId, unitQty };
      })
      .filter(Boolean);
  }

  handleClick(event) {
    const decBtn = event.target.closest?.('[data-addon-qty-dec]');
    const incBtn = event.target.closest?.('[data-addon-qty-inc]');
    if (!decBtn && !incBtn) return;

    const wrapper = (decBtn || incBtn).closest('[data-addon-qty]');
    if (!wrapper) return;

    const input = wrapper.querySelector('input[data-addon-qty-input]');
    if (!input) return;

    const maxAttr = input.getAttribute('max');
    const max = Math.max(1, parseInt(maxAttr, 10) || 999);
    const min = Math.max(1, parseInt(input.getAttribute('min'), 10) || 1);

    let nextQuantity = parseInt(input.value, 10) || 1;
    nextQuantity = Math.max(min, Math.min(max, nextQuantity));

    if (decBtn) nextQuantity = Math.max(min, nextQuantity - 1);
    if (incBtn) nextQuantity = Math.min(max, nextQuantity + 1);

    input.value = String(nextQuantity);
    this.updateAddonQtyButtonStates(wrapper);
    this.updateProductPriceDisplay();
    input.dispatchEvent(new Event('change', { bubbles: true }));
    event.preventDefault();
  }

  updateAddonQtyButtonStates(wrapper) {
    if (!wrapper || !wrapper.matches?.('[data-addon-qty]')) return;

    const input = wrapper.querySelector('input[data-addon-qty-input]');
    const decBtn = wrapper.querySelector('[data-addon-qty-dec]');
    const incBtn = wrapper.querySelector('[data-addon-qty-inc]');
    if (!input || !decBtn || !incBtn) return;

    const max = Math.max(1, parseInt(input.getAttribute('max'), 10) || 999);
    const min = Math.max(1, parseInt(input.getAttribute('min'), 10) || 1);

    const parsedQuantity = parseInt(input.value, 10);
    if (Number.isNaN(parsedQuantity)) {
      decBtn.disabled = false;
      incBtn.disabled = false;
      return;
    }

    const clampedQuantity = Math.max(min, Math.min(max, parsedQuantity));
    decBtn.disabled = clampedQuantity <= min;
    incBtn.disabled = clampedQuantity >= max;
  }

  syncAllAddonQtyButtonStates() {
    this.querySelectorAll('[data-addon-qty]').forEach((qtyWrapper) => {
      this.updateAddonQtyButtonStates(qtyWrapper);
    });
  }

  handleAddonQtyInput(event) {
    const input = event.target;
    if (!input.matches?.('input[data-addon-qty-input]')) return;

    const wrapper = input.closest('[data-addon-qty]');
    this.updateAddonQtyButtonStates(wrapper);
    this.updateProductPriceDisplay();
  }

  handleAddonQtyChange(event) {
    const input = event.target;
    if (!input.matches?.('input[data-addon-qty-input]')) return;

    const wrapper = input.closest('[data-addon-qty]');
    const max = Math.max(1, parseInt(input.getAttribute('max'), 10) || 999);
    const min = Math.max(1, parseInt(input.getAttribute('min'), 10) || 1);

    let clampedQty = parseInt(input.value, 10);
    if (Number.isNaN(clampedQty)) clampedQty = min;
    clampedQty = Math.max(min, Math.min(max, clampedQty));
    input.value = String(clampedQty);
    this.updateAddonQtyButtonStates(wrapper);
    this.updateProductPriceDisplay();
  }

  handleChange(event) {
    const input = event.target;

    if (!input.classList.contains('add-on-block__option-input')) return;

    this.syncSelectedState();

    const isVariantOption = input.classList.contains('add-on-block__option-input--variant');
    if (input.name === 'id') {
      this.updateMainVariant(input.value);
      this.updateUrlForVariant(input.value);
      this.switchProductImageToVariant(input.value);
      this.updateMainVariantDiffs();
    } else if (isVariantOption) {
      const variantId = this.resolveVariantFromOptions();
      if (variantId) {
        this.updateMainVariant(variantId);
        this.updateUrlForVariant(variantId);
        this.switchProductImageToVariant(variantId);
        this.updateMainVariantDiffs();
      }
    }

    if (input.value === 'with' || input.value === 'without') {
      this.handleAddonSelection(input);
    }

    this.updateProductPriceDisplay();
  }

  handleAddonSelection(input) {
    if (!this.form) return;

    const isWith = input.value === 'with';
    let variantId = input.dataset.variantId;

    if (!isWith && !variantId && input.name) {
      const withRadio = this.querySelector(
        `input.add-on-block__option-input[name="${input.name}"][value="with"]`
      );
      variantId = withRadio?.dataset?.variantId;
    }

    if (!variantId) return;

    const name = `addons[${variantId}]`;
    let hidden = this.form.querySelector(`input[name="${name}"]`);

    if (isWith) {
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = name;
        hidden.value = variantId;
        this.form.appendChild(hidden);
      }
    } else {
      if (hidden) hidden.remove();
    }
  }

  getMainProductQuantity() {
    if (!this.form) return 1;

    const quantityField =
      this.form.querySelector('[name="quantity"]') ||
      this.form.querySelector('[name="quantity-js"]') ||
      this.form.querySelector('input#Quantity') ||
      this.form.querySelector('.js-qty__num');

    const parsedQty = parseInt(quantityField?.value, 10);
    return Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
  }

  getMainVariantPriceCents() {
    const mainInput =
      this.form?.querySelector('[name="id"]:checked') ||
      this.form?.querySelector('[name="id"]');

    const variantId = mainInput?.value;
    if (!variantId) return 0;

    if (this._variantPriceMap?.has(String(variantId))) {
      return this._variantPriceMap.get(String(variantId));
    }

    return 0;
  }

  getAddonUnitQtyForWithInput(withInput) {
    const card = withInput.closest('.add-on-block__option')?.querySelector('.add-on-block__option-card');
    const qtyInput = card?.querySelector('input[data-addon-qty-input]');
    if (!qtyInput) return 1;

    const max = Math.max(1, parseInt(qtyInput.getAttribute('max'), 10) || 999);
    const min = Math.max(1, parseInt(qtyInput.getAttribute('min'), 10) || 1);
    let parsedQty = parseInt(qtyInput.value, 10);
    if (Number.isNaN(parsedQty)) parsedQty = min;
    return Math.max(min, Math.min(max, parsedQty));
  }

  getAddonPriceCents() {
    const checked = this.querySelectorAll(
      'input.add-on-block__option-input[value="with"]:checked[data-variant-price]'
    );

    return Array.from(checked).reduce((totalCents, withRadioInput) => {
      const variantPriceCents = parseInt(withRadioInput.dataset.variantPrice, 10);
      if (Number.isNaN(variantPriceCents)) return totalCents;
      const addonUnitQty = this.getAddonUnitQtyForWithInput(withRadioInput);
      return totalCents + variantPriceCents * addonUnitQty;
    }, 0);
  }

  updateProductPriceDisplay() {
    const mainQty = this.getMainProductQuantity();
    const total =
      (this.getMainVariantPriceCents() + this.getAddonPriceCents()) * mainQty;

    const priceEl = document.getElementById('ProductPrice');
    if (!priceEl) return;

    priceEl.textContent = this.formatMoney(total);
  }

  formatMoney(cents) {
    const stripCents = (moneyStr) => {
      if (typeof moneyStr !== 'string') return moneyStr;
      return moneyStr.replace(/[.,]\d{2}(?!.*\d)/, '').trim();
    };

    if (
      typeof window.wetheme !== 'undefined' &&
      typeof window.wetheme.formatMoney === 'function'
    ) {
      const formatted = this._stripHtml(window.wetheme.formatMoney(cents));
      return stripCents(formatted);
    }

    if (
      typeof window.Shopify !== 'undefined' &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      const formatted = this._stripHtml(
        window.Shopify.formatMoney(cents, window.Shopify.money_format)
      );
      return stripCents(formatted);
    }

    return String(Math.trunc(cents / 100));
  }

  _stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  syncSelectedState() {
    const inputs = this.querySelectorAll('.add-on-block__option-input');

    inputs.forEach(input => {
      const card = input
        .closest('.add-on-block__option')
        ?.querySelector('.add-on-block__option-card');

      if (card) {
        card.classList.toggle('is-selected', input.checked);
      }
    });

    this.syncAllAddonQtyButtonStates();
  }

  updateMainVariant(variantId) {
    if (!this.form) return;

    const id = String(variantId);

    const mainInputs = this.form.querySelectorAll('[name="id"]');
    let targetInput = mainInputs.length
      ? Array.from(mainInputs).find((input) => input.value === id)
      : null;
    if (targetInput) {
      if (targetInput.type === 'radio' || targetInput.type === 'checkbox') {
        if (!targetInput.checked) targetInput.checked = true;
      } else {
        targetInput.value = id;
      }
    } else {
      const existingIdInput = this.form.querySelector('[name="id"]');
      if (existingIdInput && existingIdInput.type !== 'radio' && existingIdInput.type !== 'checkbox') {
        existingIdInput.value = id;
      } else {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'id';
        hidden.value = id;
        this.form.appendChild(hidden);
      }
    }

    const product = this._productCache;
    const variant = product?.variants?.find((productVariant) => productVariant.id === parseInt(variantId, 10));
    if (variant) {
      this.syncThemeVariantOptions(variant);
    }

    const variantSelect = this.form.querySelector(
      'select.product-single__variants, select[id^="productSelect-"]'
    );
    if (variantSelect && variantSelect.value !== id) {
      const hasOption = Array.from(variantSelect.options).some(
        (opt) => opt.value === id
      );
      if (hasOption) {
        variantSelect.value = id;
        variantSelect.dispatchEvent(new Event('input', { bubbles: true }));
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  syncThemeVariantOptions(variant) {
    if (!this.form) return;
    const options = [variant.option1, variant.option2, variant.option3].filter(
      (optionValue) => optionValue != null && optionValue !== ''
    );
    const escapedValues = options.map((v) => CSS.escape(String(v)));
    options.forEach((value, index) => {
      const name = `option-${index + 1}`;
      const input = this.form.querySelector(
        `input[name="${name}"][value="${escapedValues[index]}"]`
      );
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      const optionSelects = this.form.querySelectorAll('select[id^="SingleOptionSelector-"]');
      const select = optionSelects[index];
      if (select && select.value !== value) {
        const option = Array.from(select.options).find(
          (opt) => opt.value === value || opt.textContent.trim() === value
        );
        if (option) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          select.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
  }

  updateUrlForVariant(variantId) {
    if (!variantId || typeof window.history.replaceState !== 'function') return;

    const search = window.location.search;
    const params = search.length > 1
      ? search.slice(1).split('&').filter((param) => !param.startsWith('variant='))
      : [];
    params.push('variant=' + String(variantId));
    const query = params.join('&');
    const url = window.location.pathname + (query ? '?' + query : '');

    window.history.replaceState({ path: url }, '', url);
  }

  switchProductImageToVariant(variantId) {
    const product = this._productCache;
    if (!product?.variants?.length || !product?.media?.length) return;

    const variant = product.variants.find(
      (productVariant) => productVariant.id === parseInt(variantId, 10)
    );
    if (!variant?.featured_media?.id) return;

    const mediaId = variant.featured_media.id;
    const mediaIndex = product.media.findIndex((mediaItem) => mediaItem.id === mediaId);
    if (mediaIndex < 0) return;

    const mediaEls = Array.from(document.querySelectorAll(`[data-media-id="${mediaId}"]`));
    mediaEls.length && mediaEls.forEach((mediaElement) => mediaElement.click());

    if (this.form) {
      this.form.dispatchEvent(
        new CustomEvent('product-media-select', {
          bubbles: true,
          detail: { index: mediaIndex, forceLoad: true }
        })
      );
    }
  }

  updateMainVariantDiffs() {
    const allInputs = Array.from(this.querySelectorAll(
      'input.add-on-block__option-input--variant[data-variant-price]'
    ));

    if (!allInputs.length) return;

    const groups = new Map();
    allInputs.forEach((variantOptionInput) => {
      const groupKey = variantOptionInput.name;
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey).push(variantOptionInput);
    });

    groups.forEach((groupInputs) => {
      const checkedInput = groupInputs.find((optionInput) => optionInput.checked);
      if (!checkedInput) return;

      const basePrice = parseInt(checkedInput.dataset.variantPrice, 10);
      if (Number.isNaN(basePrice)) return;

      groupInputs.forEach((optionInput) => {
        const priceEl = optionInput
          .closest('.add-on-block__option')
          ?.querySelector('.add-on-block__option-price');

        if (!priceEl) return;

        if (optionInput.checked) {
          priceEl.textContent = '';
          return;
        }

        const optionPrice = parseInt(optionInput.dataset.variantPrice, 10);
        if (Number.isNaN(optionPrice)) return;

        const diff = optionPrice - basePrice;

        if (diff === 0) {
          priceEl.textContent = '';
          return;
        }

        if (diff < 0) {
          const zeroFormatted = this.formatMoney(0);
          priceEl.textContent = `+ ${zeroFormatted}`;
          return;
        }

        const formatted = this.formatMoney(Math.abs(diff));
        priceEl.textContent = `+ ${formatted}`;
      });
    });
  }

  handleMoreInfoClick(event) {
    event.preventDefault();
    const anchor = event.currentTarget;
    const popupId = anchor.getAttribute('data-popup-id');
    if (!popupId) return;
    const popupElement = document.getElementById(popupId);
    if (!popupElement) return;

    if (typeof popupElement.open === 'function') {
      popupElement.open();
      return;
    }

    const dialog = popupElement.querySelector('dialog');
    if (!dialog) return;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
      return;
    }

    if (typeof dialog.show === 'function') {
      dialog.show();
    }
  }
}

if (!customElements.get('add-on-block')) {
  customElements.define('add-on-block', AddOnBlock);
}
